import { collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore/lite';
import { chunk } from 'lodash';

import { BaseStatData, StatData, StatType } from '../types/statData';
import { firestore } from '../utils/firebase';

export type NewStatData = Omit<StatData, 'id' | 'created'>;

export default class StatApi {
  private static readonly collectionReference = collection(firestore, 'stats');

  public static async addStat(stat: NewStatData) {
    const docRef = doc(this.collectionReference);
    await setDoc(docRef, {
      ...stat,
      id: docRef.id,
      created: serverTimestamp(),
    });
    return docRef.id;
  }

  public static async getStatWithTargetIdAndUserId(targetId: string, userUid: string) {
    const querySnap = await getDocs(
      query(this.collectionReference, where('targetId', '==', targetId), where('userUid', '==', userUid))
    );
    if (querySnap.docs.length === 0) return undefined;
    const docSnap = querySnap.docs[0];
    return {
      ...docSnap.data(),
      id: docSnap.id,
      created: docSnap.data().created.toDate(),
    } as BaseStatData;
  }

  public static async getStatsWithTargetIdsAndUserId(targetIds: string[], userUid: string) {
    if (targetIds.length === 0) return [];

    const targetIdChunks = chunk(targetIds, 10);
    const querySnapPromises = targetIdChunks.map(targetIdChunk =>
      getDocs(query(this.collectionReference, where('userUid', '==', userUid), where('targetId', 'in', targetIdChunk)))
    );
    const querySnaps = await Promise.all(querySnapPromises);
    const statChunks = querySnaps.map(querySnap =>
      querySnap.docs.map(docSnap => {
        return {
          ...docSnap.data(),
          id: docSnap.id,
          created: docSnap.data().created.toDate(),
        } as StatData;
      })
    );
    return statChunks.flat();
  }

  public static async get() {}

  public static async getSectionStatsBySkillId(skillIId: string) {
    const querySnap = await getDocs(
      query(this.collectionReference, where('skillId', '==', skillIId), where('type', '==', StatType.Section))
    );
    return querySnap.docs.map(docSnap => {
      return {
        ...docSnap.data(),
        id: docSnap.id,
        created: docSnap.data().created.toDate(),
      } as StatData;
    });
  }

  public static async getStatsWithTypeAndUserId(type: string, userUid: string) {
    const querySnap = await getDocs(
      query(this.collectionReference, where('type', '==', type), where('userUid', '==', userUid))
    );
    return querySnap.docs.map(docSnap => {
      return {
        ...docSnap.data(),
        id: docSnap.id,
        created: docSnap.data().created.toDate(),
      } as BaseStatData;
    });
  }
}
